import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Clock, Info, Megaphone, ShieldAlert, WifiOff, X } from 'lucide-react';
import { formatDate } from '@dezza/shared';
import { cn, useOnline } from '@dezza/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Tone = 'info' | 'warning' | 'danger' | 'dark' | 'success';

const TONES: Record<Tone, string> = {
  info: 'bg-info-soft text-info',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-solid text-on-danger',
  dark: 'bg-dark text-on-dark',
  success: 'bg-success-soft text-success',
};

const linkClass =
  'font-semibold underline decoration-current/40 underline-offset-[3px] transition-[text-decoration-color] hover:decoration-current';

function Bar({
  tone,
  icon,
  children,
  onClose,
}: {
  tone: Tone;
  icon: ReactNode;
  children: ReactNode;
  onClose?: () => void;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-3 border-b border-line px-4 py-2 text-[13.5px] leading-snug sm:px-6',
        TONES[tone],
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-current/10 [&_svg]:size-3.5"
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1 font-medium">{children}</div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar aviso"
          className="-mr-1.5 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-current/10 focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-4"
        >
          <X />
        </button>
      ) : null}
    </div>
  );
}

function daysUntil(iso: string | null | undefined): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));
}

/** Avisos globais no topo do conteúdo (modo suporte, sem internet, assinatura, e-mail e comunicados). */
export function Banners() {
  const { me, logout } = useAuth();
  const online = useOnline();
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('dz.banners') ?? '[]') as string[];
    } catch {
      return [];
    }
  });
  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      sessionStorage.setItem('dz.banners', JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  if (!me) return null;
  const ent = me.entitlements;
  const canBill = me.membership?.permissions.includes('billing.manage');
  const trialDays = daysUntil(ent?.trialEndsAt);

  return (
    <div>
      {me.impersonation ? (
        <Bar tone="danger" icon={<ShieldAlert />}>
          <span>
            Modo suporte: {me.impersonation.adminName} está acessando esta conta até{' '}
            {new Date(me.impersonation.expiresAt).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
            .
          </span>{' '}
          <button
            type="button"
            className={linkClass}
            onClick={() => void logout().then(() => window.close())}
          >
            Encerrar acesso
          </button>
        </Bar>
      ) : null}
      {!online ? (
        <Bar tone="dark" icon={<WifiOff />}>
          Você está sem internet. Pode continuar vendendo: sincronizamos quando a conexão voltar.
        </Bar>
      ) : null}
      {me.company?.deletionScheduledFor ? (
        <Bar tone="danger" icon={<AlertTriangle />}>
          Exclusão da loja agendada para {formatDate(me.company.deletionScheduledFor)}.{' '}
          <Link className={linkClass} to="/configuracoes/privacidade">
            Cancelar exclusão
          </Link>
        </Bar>
      ) : null}
      {ent?.status === 'past_due' ? (
        <Bar tone="danger" icon={<AlertTriangle />}>
          Não identificamos o pagamento da sua assinatura.{' '}
          {canBill ? (
            <Link className={linkClass} to="/configuracoes/plano">
              Regularizar agora
            </Link>
          ) : (
            'Avise o responsável pela loja.'
          )}
        </Bar>
      ) : null}
      {ent?.status === 'trialing' && !dismissed.includes('trial') ? (
        <Bar tone="warning" icon={<Clock />} onClose={() => dismiss('trial')}>
          Teste grátis do plano {ent.planName}: {trialDays === 1 ? 'falta 1 dia' : `faltam ${trialDays} dias`}
          .{' '}
          {canBill ? (
            <Link className={linkClass} to="/configuracoes/plano">
              Escolher plano
            </Link>
          ) : null}
        </Bar>
      ) : null}
      {ent?.downgraded && !dismissed.includes('downgraded') ? (
        <Bar tone="info" icon={<Info />} onClose={() => dismiss('downgraded')}>
          Sua loja está no plano Grátis. Seus dados continuam salvos.{' '}
          {canBill ? (
            <Link className={linkClass} to="/configuracoes/plano">
              Reativar recursos
            </Link>
          ) : null}
        </Bar>
      ) : null}
      {!me.user.emailVerified && !dismissed.includes('verify') ? (
        <Bar tone="info" icon={<Info />} onClose={() => dismiss('verify')}>
          Confirme seu e-mail ({me.user.email}) para proteger sua conta.{' '}
          <button
            type="button"
            className={linkClass}
            onClick={() => void api.post('/auth/resend-verification').then(() => dismiss('verify'))}
          >
            Reenviar link
          </button>
        </Bar>
      ) : null}
      {me.announcements
        .filter((a) => !dismissed.includes(`a:${a.id}`))
        .map((a) => (
          <Bar
            key={a.id}
            tone={
              a.level === 'critical'
                ? 'danger'
                : a.level === 'warning'
                  ? 'warning'
                  : a.level === 'success'
                    ? 'success'
                    : 'info'
            }
            icon={<Megaphone />}
            onClose={() => dismiss(`a:${a.id}`)}
          >
            <strong className="font-semibold">{a.title}</strong> {a.body}{' '}
            {a.link ? (
              <a className={linkClass} href={a.link} target="_blank" rel="noreferrer">
                Saiba mais
              </a>
            ) : null}
          </Bar>
        ))}
    </div>
  );
}
